import { DefaultErrorMessages } from "@helpers/formValidationHelper";
import { Organization, OrganizationStatus } from "@helpers/organizationHelper";
import { yupResolver } from "@hookform/resolvers/yup";
import { UserStatus } from "@store/generalTypes";
import { isEmpty } from "lodash";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import { object, SchemaOf, string } from "yup";
import { LBtn, LInputField, LInputRow, LTextareaField } from "@components";
import { NavItems } from "@helpers/navigation";
import { useApi } from "@services/useApi";
import { CountrySelectOptions } from "../components/CountrySelectOptions";
import { SignupContainer } from "../components/SignupContainer";
import { TitleSelectOptions } from "../components/TitleSelectOptions";
import { useAppSelector } from "@app/hooks";
import { InputFieldProps } from "@components/LInputField";
import { User } from "@store/userInfoSlice";

interface ContactSalesForm {
    email: string;
    firstName: string;
    lastName: string;
    title?: string;
    jobTitle: string;
    otherJobTitle?: string;
    phoneNumber: string;
    orgName: string;
    country?: string;
    otherCountry?: string;
    details?: string;
}

const formSchema: SchemaOf<ContactSalesForm> = object({
    email: string().email().required("* required"),
    firstName: string().required("* required"),
    lastName: string().required("* required"),
    title: string().optional(),
    jobTitle: string().required("* required"),
    otherJobTitle: string().when("jobTitle", (val, schema) => {
        return val === "Other" ? schema.required("Other job title is required") : schema;
    }),
    phoneNumber: string()
        .required(DefaultErrorMessages.required)
        .matches(/^\d+$/, DefaultErrorMessages.numeric("Phone number"))
        .length(10, DefaultErrorMessages.lengthDigits(10, "Phone number")),
    orgName: string().required("* required"),
    country: string().required("* required"),
    otherCountry: string().when("country", (val, schema) => {
        return val === "Other" ? schema.required("Other country is required") : schema;
    }),
    details: string().optional()
});

export const ContactSales = (): JSX.Element => {
    const navigate = useNavigate();
    const validateMethods = useForm<ContactSalesForm>({
        resolver: yupResolver(formSchema)
    });

    const { getUser, createUser, createOrganization, sendEmailContactSales, loading } = useApi();

    const { userDetail, organization } = useAppSelector((x) => {
        return { userDetail: x.userDetail, organization: x.organization };
    });
    const hasUser = !isEmpty(userDetail?.id);

    if (hasUser) {
        validateMethods.setValue("email", userDetail.email);
        validateMethods.setValue("firstName", userDetail.firstName);
        validateMethods.setValue("lastName", userDetail.lastName);
        validateMethods.setValue("jobTitle", userDetail.jobTitle);
        validateMethods.setValue("orgName", organization?.orgName ?? "-");
        validateMethods.setValue(
            "country",
            organization?.address.country === "United States" ? organization?.address.country : "Other"
        );
        validateMethods.setValue("otherCountry", organization?.address.country ?? "");
        validateMethods.setValue("phoneNumber", organization?.primaryContact?.phoneNumber ?? "");
    }

    const formSubmitHandler: SubmitHandler<ContactSalesForm> = async (data: ContactSalesForm) => {
        let user = {} as User;
        let org = { primaryContact: {}, address: {} } as Organization;

        if (hasUser) {
            user.email = userDetail.email;
            user.firstName = userDetail.firstName;
            user.jobTitle = userDetail.jobTitle;
            user.lastName = userDetail.lastName;
            user.title = userDetail.title;

            org.orgName = data.orgName;
            org.primaryContact!.phoneNumber = data.phoneNumber;
            org.address.country = data.country === "Other" ? data.otherCountry : data.country;
            org.details = data.details;
        } else {
            const emailUser = await getUser(data.email);

            user.id = emailUser.id;
            user.firstName = emailUser.firstName;
            user.lastName = emailUser.lastName;
            user.email = data.email;

            if (isEmpty(user.id)) {
                user = await createUser({
                    email: data.email,
                    firstName: data.firstName,
                    jobTitle: data.jobTitle,
                    lastName: data.lastName,
                    otherJobTitle: data.otherJobTitle,
                    password: `P2ssw0rd-${Math.round(Math.random() * 100)}`,
                    status: UserStatus.Inactive,
                    title: data.title
                });
            }

            org = {
                userId: user.id!,
                address: {
                    city: "",
                    postalCode: "",
                    stateCode: "",
                    streetAddress: "",
                    streetAddress2: "",
                    country: data.country === "Other" ? data.otherCountry : data.country
                },
                orgName: data.orgName,
                primaryContact: {
                    email: data.email,
                    firstName: data.firstName,
                    lastName: data.lastName,
                    phoneNumber: data.phoneNumber
                },
                details: data.details,
                status: OrganizationStatus.inactive
            };

            if (!emailUser?.hasOrganization) {
                await createOrganization(org);
            }
        }

        await sendEmailContactSales(org, user, data.title!, data.jobTitle, data.country!, data.details!);

        navigate(NavItems.Signup.ContactConfirm.path, { state: { hasUser } });
    };

    const nameGroup = [
        {
            type: "text",
            label: "Title",
            inputName: "title",
            isSmall: true,
            defaultValue: userDetail.title,
            value: userDetail.title,
            disabled: hasUser
        } as InputFieldProps,
        {
            type: "text",
            label: "First Name",
            inputName: "firstName",
            placeholder: "e.g. John",
            isExpanded: true,
            defaultValue: userDetail.firstName,
            value: userDetail.firstName,
            disabled: hasUser
        } as InputFieldProps,
        {
            type: "text",
            label: "Last Name",
            inputName: "lastName",
            placeholder: "e.g. Smith",
            isExpanded: true,
            defaultValue: userDetail.lastName,
            value: userDetail.lastName,
            disabled: hasUser
        } as InputFieldProps
    ];

    return (
        <FormProvider {...validateMethods}>
            <SignupContainer>
                <form onSubmit={validateMethods.handleSubmit(formSubmitHandler)}>
                    <p className="is-size-5 has-text-weight-bold mb-4">
                        <span className="has-text-primary-blue mr-3">1</span>
                        Main Contact Information
                    </p>
                    <LInputField
                        type="email"
                        label="Organization email address"
                        inputName="email"
                        placeholder="e.g. alex@example.com"
                        disabled={hasUser}
                        defaultValue={userDetail.email}
                        value={userDetail.email}
                    />
                    <LInputRow inputs={nameGroup} />
                    <TitleSelectOptions
                        value={hasUser && !!userDetail.jobTitle ? "Other" : userDetail.jobTitle}
                        otherJobTitle={userDetail.jobTitle}
                        disabled={hasUser && !!userDetail.jobTitle}
                        hideSelect={hasUser && !!userDetail.jobTitle}
                    />
                    <LInputField
                        label="Phone Number"
                        inputName="phoneNumber"
                        placeholder="Enter phone number"
                        value={organization?.primaryContact?.phoneNumber}
                        defaultValue={organization?.primaryContact?.phoneNumber}
                        maxLength={10}
                    />

                    <p className="is-size-5 has-text-weight-bold mb-4">
                        <span className="has-text-primary-blue mr-3">2</span>
                        Organization information
                    </p>
                    <LInputField
                        label="Organization name"
                        inputName="orgName"
                        placeholder="Enter organization name"
                        disabled={hasUser}
                        value={organization?.orgName}
                        defaultValue={organization?.orgName}
                    />
                    <CountrySelectOptions
                        value={
                            organization?.address.country === "United States" ? organization?.address.country : "Other"
                        }
                        otherCountry={organization?.address.country}
                        disabled={hasUser && !!organization?.address.country}
                    />
                    <LTextareaField
                        label="Additional details (optional)"
                        inputName="details"
                        placeholder="E.g. customized legal requirements, special purchasing process, etc."
                        defaultValue={""}
                    />
                    <LBtn noSpacing type="submit" loading={loading} classes="mt-4" isBold notRounded>
                        {"Submit"}
                    </LBtn>
                </form>
            </SignupContainer>
        </FormProvider>
    );
};
